import { useState } from "react";
import { Check, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { lineForFeature, PROFESSIONAL_FEATURES, ANNUAL_SAVING_LINE } from "@/lib/upgradeCopy";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface UpgradePromptProps {
  feature: string;
  description?: string;
}

export function UpgradePrompt({ feature, description }: UpgradePromptProps) {
  const navigate = useNavigate();
  const personalLine = description || lineForFeature(feature);
  const [featuresOpen, setFeaturesOpen] = useState(false);

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      <div className="w-full max-w-xl">
        <div className="rounded-xl p-6 border" style={{ background: "#ECFDF5", borderColor: "#52B788" }}>
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg" style={{ background: "rgba(82,183,136,0.15)" }}>
              <Lock className="h-5 w-5" style={{ color: "#52B788" }} />
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-body uppercase tracking-wide text-nursly-muted mb-1">
                🔒 This is on the Professional plan
              </p>
              <h3 className="font-heading font-semibold text-lg text-nursly-forest mb-2">
                {feature} is available on Professional
              </h3>
              <p className="text-sm font-body text-nursly-ink mb-4">{personalLine}</p>
              <p className="text-xs font-body text-nursly-muted mb-4">
                From £49 a month, or 2 months free when you pay yearly. · 14-day money-back on annual · Free migration included
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={() => navigate("/upgrade")} className="font-body">
                  Upgrade to Professional →
                </Button>
                <Button variant="outline" onClick={() => setFeaturesOpen(true)} className="font-body">
                  See all features
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-body text-nursly-muted">
                <span>✓ Cancel at any time</span>
                <span>✓ 14-day money-back on annual</span>
                <span>✓ Free migration included</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={featuresOpen} onOpenChange={setFeaturesOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Everything on Professional</DialogTitle>
            <DialogDescription>
              Monthly: £49 a month. Annual: £490 a year. {ANNUAL_SAVING_LINE}.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2 text-sm font-body text-nursly-ink">
            {PROFESSIONAL_FEATURES.map((f) => (
              <li key={f} className="flex gap-2">
                <Check className="h-4 w-4 text-nursly-sage shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" className="h-11" onClick={() => setFeaturesOpen(false)}>
              Close
            </Button>
            <Button className="h-11" onClick={() => navigate("/upgrade")}>
              Upgrade to Professional →
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Inline banner version for use at top of partially-accessible pages */
export function UpgradeBanner({ feature }: { feature: string }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl p-4 mb-4 border flex items-center justify-between" style={{ background: "#ECFDF5", borderColor: "#B7E4C7" }}>
      <div className="flex items-center gap-3">
        <Lock className="h-4 w-4" style={{ color: "#52B788" }} />
        <span className="text-sm font-body font-medium text-nursly-forest">
          {feature} is available on Professional
        </span>
      </div>
      <Button size="sm" onClick={() => navigate("/upgrade")} className="font-body">
        Upgrade
      </Button>
    </div>
  );
}
